import React from "react";
import { motion, AnimatePresence } from "framer-motion";

// Status indicator shape per agent
function StatusIcon({ status }) {
  if (status === "completed") return <div className="w-2 h-2 bg-[#00B4E6]" />;
  if (status === "error") return <div className="w-2 h-2 bg-[#FF5B5B]" />;
  if (status === "thinking" || status === "working")
    return (
      <div className="relative w-2 h-2">
        <div className="w-2 h-2 bg-[#00B4E6] rounded-full" />
        <span className="pulse-ring" />
      </div>
    );
  // idle
  return <div className="w-2 h-2 border border-[#A3A39A]" />;
}

const providerLabel = {
  anthropic: "Claude · Sonnet 4.5",
  openai: "OpenAI · GPT-5.2",
  gemini: "Gemini · 3 Flash",
};

export default function AgentNode({ agent, idx, onOpen, isActive }) {
  const status = agent.status || "idle";
  const wordCount = (agent.output || "").trim().split(/\s+/).filter(Boolean).length;

  return (
    <motion.button
      type="button"
      onClick={() => onOpen(agent.id)}
      data-testid={`agent-node-${agent.id}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: idx * 0.04 }}
      whileHover={{ backgroundColor: "rgba(0,180,230,0.04)" }}
      className={
        "group text-left p-5 lg:p-6 border hairline relative h-full flex flex-col justify-between min-h-[150px] " +
        (isActive ? "bg-[#00B4E608]" : "bg-transparent")
      }
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <StatusIcon status={status} />
          <div className="label-xs">{String(agent.layer).padStart(2, "0")} · {agent.layer_name}</div>
        </div>
        <div className="label-xs text-right opacity-60 hidden md:block">
          {providerLabel[agent.provider] || agent.provider}
        </div>
      </div>

      <div className="mt-3">
        <div className="font-serif-display text-2xl lg:text-[26px] leading-[1.05] tracking-tight">
          {agent.name}
        </div>
      </div>

      <div className="mt-4 flex items-end justify-between gap-3">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#A8B3C7]">
          {status === "thinking" && "Spinning up…"}
          {status === "working" && `Drafting · ${wordCount}w`}
          {status === "completed" && `Complete · ${wordCount}w`}
          {status === "error" && "Errored"}
          {status === "idle" && "Standing by"}
        </div>
        <div className="font-mono text-[11px] opacity-70 group-hover:opacity-100 group-hover:text-[#00B4E6] transition-colors">
          open →
        </div>
      </div>

      {/* live token sliver for working/thinking */}
      <AnimatePresence>
        {(status === "working" || status === "thinking") && agent.output && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute left-0 right-0 bottom-0 px-5 lg:px-6 py-2 font-mono text-[11px] text-[#A8B3C7] truncate bg-[#010f23]/80 border-t hairline"
          >
            {(agent.output || "").slice(-110)}
            <span className="caret" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

export { providerLabel };
